import type ArrayProxy from '@ember/array/proxy';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import type PluginInstallModel from 'garaje/models/plugin-install';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { DiplomatAblePluginOption } from 'garaje/utils/binary-download-options';
import { DIPLOMAT_ABLE_PLUGIN_OPTIONS } from 'garaje/utils/binary-download-options';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { wrappedFetch } from 'garaje/utils/wrapped-fetch';
import zft from 'garaje/utils/zero-for-tests';

interface DiplomatConfigCreateDiplomatClientComponentArgs {
  pluginInstalls: ArrayProxy<PluginInstallModel>;
  onClose?: () => unknown;
  onUpdateClient: () => Promise<void>;
  currentLocation: Model;
}

export default class DiplomatConfigCreateDiplomatClientComponent extends Component<DiplomatConfigCreateDiplomatClientComponentArgs> {
  @service declare flashMessages: FlashMessagesService;

  @tracked diplomatEnabledPluginsOptions: DiplomatAblePluginOption[] = [];
  @tracked selectedIntegration?: DiplomatAblePluginOption;
  @tracked clientId?: string;
  @tracked internalUrl?: string;

  diplomatAblePluginOptions: DiplomatAblePluginOption[] = DIPLOMAT_ABLE_PLUGIN_OPTIONS;

  get disabled(): boolean {
    return (
      this.selectedIntegration === undefined ||
      this.clientId === undefined ||
      this.clientId === '' ||
      this.internalUrl === undefined ||
      this.internalUrl === ''
    );
  }

  get diplomatAblePluginInstall(): number | undefined {
    const selectedPluginId = this.selectedIntegration?.id;
    const validPluginInstall = this.args.pluginInstalls.find(
      (pluginInstall: PluginInstallModel) =>
        (pluginInstall.status === 'active' || pluginInstall.status === 'pending') &&
        // eslint-disable-next-line ember/use-ember-get-and-set
        pluginInstall.plugin.get('id') === selectedPluginId,
    );

    return validPluginInstall ? Number.parseInt(validPluginInstall.id) : undefined;
  }

  constructor(owner: unknown, args: DiplomatConfigCreateDiplomatClientComponentArgs) {
    super(owner, args);
  }

  createDiplomatClientTask = task(async (clientId: string) => {
    if (this.disabled) return;

    const plugin_install_id = this.diplomatAblePluginInstall;
    await timeout(zft(1000));

    if (plugin_install_id === undefined) {
      this.flashMessages.showAndHideFlash(
        'error',
        `${this.selectedIntegration?.name} is not installed at this location`,
      );
      return;
    }

    try {
      const url = urlBuilder.diplomatServer.saveDiplomatClientUrl();
      await wrappedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
        body: JSON.stringify({
          id: clientId,
          location_id: Number.parseInt(this.args.currentLocation.id),
          enabled: false,
          plugin_install_id,
          plugin_id: this.selectedIntegration?.id,
          internal_url: this.internalUrl,
        }),
        credentials: 'include',
      });
      await this.args.onUpdateClient();
      this.onClose();
      this.flashMessages.showAndHideFlash('success', 'Successfully created.');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  setSelectedIntegration(option: DiplomatAblePluginOption): void {
    this.selectedIntegration = option;
  }

  @action
  setClientId(clientId: string): void {
    this.clientId = clientId;
  }

  @action
  setInternalUrl(internalUrl: string): void {
    this.internalUrl = internalUrl;
  }

  @action
  onClose(): void {
    this.args.onClose?.();
  }
}
